import axios from 'axios';
import { API_CONFIG } from '../utils/constants';
import authClient from './authClient';
import { getBrowserSecurityHeaders, clearDeviceId } from '../utils/browserFingerprint';
import { appNavigate } from '../utils/navigationRef';

// Helper function to check if token is expired or expiring soon
const isTokenExpiringSoon = (): boolean => {
  const tokenExpiresAt = localStorage.getItem('tokenExpiresAt');
  if (!tokenExpiresAt) {
    return false; // No expiry info, let the request proceed
  }

  const expiryTime = parseInt(tokenExpiresAt, 10);
  const now = Date.now();
  const timeUntilExpiry = expiryTime - now;
  const FIVE_MINUTES = 5 * 60 * 1000; // 5 minutes in milliseconds

  // Return true if token expires in less than 5 minutes
  return timeUntilExpiry < FIVE_MINUTES;
};

// Helper function to refresh token proactively
let refreshPromise: Promise<string> | null = null;

const refreshTokenIfNeeded = async (): Promise<string | null> => {
  const token = localStorage.getItem('authToken');
  
  if (!token) {
    return null;
  }

  // Check if token is expiring soon
  if (!isTokenExpiringSoon()) {
    return token; // Token is still valid, no refresh needed
  }

  // If a refresh is already in progress, wait for it
  if (refreshPromise) {
    console.log('⏳ [httpClient] Refresh already in progress, waiting...');
    return refreshPromise;
  }

  // Start refresh
  console.log('🔄 [httpClient] Token expiring soon, proactively refreshing...');
  
  refreshPromise = (async () => {
    try {
      const refreshResponse = await authClient.post('/auth/refresh');
      const newToken = refreshResponse.data.access_token;
      const expiresIn = refreshResponse.data.expires_in;
      
      // Update stored token and expiry
      localStorage.setItem('authToken', newToken);
      const expiresAt = Date.now() + (expiresIn * 1000);
      localStorage.setItem('tokenExpiresAt', expiresAt.toString());
      
      console.log('✅ [httpClient] Token refreshed proactively');
      refreshPromise = null;
      return newToken;
    } catch (error) {
      console.error('❌ [httpClient] Proactive refresh failed:', error);
      refreshPromise = null;
      
      localStorage.removeItem('authToken');
      localStorage.removeItem('tokenExpiresAt');
      clearDeviceId();
      appNavigate('/login', { replace: true });
      throw error;
    }
  })();

  return refreshPromise;
};

// Create axios instance with default configuration
const httpClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor with proactive token refresh
httpClient.interceptors.request.use(
  async (config) => {
    // Check if token is expiring soon and refresh if needed
    const token = await refreshTokenIfNeeded();
    
    // Add auth token to request
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add browser security headers for browser-only API access
    const browserHeaders = getBrowserSecurityHeaders();
    Object.entries(browserHeaders).forEach(([key, value]) => {
      config.headers[key] = value;
    });
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor with reactive token refresh (fallback)
httpClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle session invalidated due to new device login
    if (error.response?.data?.reason === 'session_invalidated_new_device' ||
        error.response?.data?.error === 'session_invalidated_new_device') {
      console.warn('🔒 [httpClient] Session invalidated - logged in from another device');
      
      // Clear all auth data
      localStorage.removeItem('authToken');
      localStorage.removeItem('tokenExpiresAt');
      clearDeviceId();
      
      // Dispatch custom event for UI to show modal
      window.dispatchEvent(new CustomEvent('session-invalidated', {
        detail: { reason: 'new_device' }
      }));
      
      return Promise.reject(error);
    }

    // Handle token refresh for 401 errors (reactive fallback)
    if (
      error.response?.status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      try {
        console.log('🔄 [httpClient] Token expired (401), attempting refresh...');
        
        // Try to refresh the token using authClient
        const refreshResponse = await authClient.post('/auth/refresh');
        const newToken = refreshResponse.data.access_token;
        const expiresIn = refreshResponse.data.expires_in;
        
        // Update stored token and expiry
        localStorage.setItem('authToken', newToken);
        const expiresAt = Date.now() + (expiresIn * 1000);
        localStorage.setItem('tokenExpiresAt', expiresAt.toString());
        
        console.log('✅ [httpClient] Token refreshed successfully');
        
        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return httpClient(originalRequest);
      } catch (refreshError) {
        console.error('❌ [httpClient] Token refresh failed:', refreshError);
        
        localStorage.removeItem('authToken');
        localStorage.removeItem('tokenExpiresAt');
        clearDeviceId();
        appNavigate('/login', { replace: true });
        return Promise.reject(refreshError);
      }
    }
    
    // Log error for debugging
    console.error('API Error:', error.response?.data || error.message);
    
    return Promise.reject(error);
  }
);

export default httpClient;
